import { useState } from "react";

import { Controller } from "react-hook-form";
import { useLocalization } from "@fluent/react";
import { entities } from "byzantine";
import { useSwiftWireDestinationForm } from "byzantine/src/dbbl/businessLogic/entities/recipients";
import { useInternationalWireLookups } from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";
import { parseErrorResponse } from "byzantine/src/dbbl/composites/request/request.helpers";
import { Alert, ResponsiveFlex, Row, Toggle } from "@narmi/design_system";

import SectionActions from "../../../common/SectionActions";
import SectionError from "../../../common/SectionError";

import AddressFields from "./AddressFields";
import BeneficiaryBankFields from "./BeneficiaryBankFields";
import CorrespondentBankFields from "./CorrespondentBankFields";

import type { SwiftAddressField } from "byzantine/src/dbbl/businessLogic/modules/recipientDrawer";
import type { AddressErrors } from "./AddressFields";

// the API's errors from the last save: lists by field, nested for addresses
type SaveErrors = Record<string, string[] | AddressErrors | undefined>;

interface InternationalWireFormProps {
  recipientId?: API.RecipientId;
  // the flow needs an international wire and this recipient doesn't have it yet
  showMissingDetailsAlert: boolean;
  // input the user hasn't saved is blocking another section
  showUnsavedError: boolean;
  onSaved: () => void;
  onCancel: () => void;
}

// saves the recipient's international wire (SWIFT) details with their own API call
const InternationalWireForm = ({
  recipientId,
  showMissingDetailsAlert,
  showUnsavedError,
  onSaved,
  onCancel,
}: InternationalWireFormProps) => {
  const { l10n } = useLocalization();
  const recipient = entities.recipients.useRecipient(recipientId);
  const form = useSwiftWireDestinationForm(recipientId);
  const {
    control,
    reset,
    getValues,
    handleSubmit,
    handleCreate,
    formErrors,
    setFormErrors,
    loading,
  } = form;
  const lookups = useInternationalWireLookups(
    form,
    recipient?.swift_destination
  );
  const [errorMessage, setErrorMessage] = useState<string>();
  const saveErrors = formErrors as SaveErrors | null;

  const getFieldError = (name: string) => {
    const error = saveErrors?.[name];
    return Array.isArray(error) ? error[0] : undefined;
  };

  const handleSaveError = async (error: unknown) => {
    if (typeof error === "string") {
      setErrorMessage(error);
      return;
    }
    const errorResponse = error as Parameters<typeof parseErrorResponse>[0];
    if (!errorResponse?.response) {
      setErrorMessage(
        (error as Error | undefined)?.message ||
          l10n.getString("error-form-generic")
      );
      return;
    }
    try {
      // field errors show under their inputs
      const body = await errorResponse.response.json();
      if (body && typeof body === "object") {
        setFormErrors(body as API.CreateUpdateSwiftDestination.Error);
      }
    } catch {
      // the message below falls back to a generic one
    }
    // a nested field error (e.g. an address's) parses to an object, which stays under its fields
    const { errors } = await parseErrorResponse(errorResponse);
    const description: unknown = errors[0]?.description;
    setErrorMessage(
      typeof description === "string" && description
        ? description
        : l10n.getString("error-form-generic")
    );
  };

  const handleClickSave = () => {
    setErrorMessage(undefined);
    // the last save's API errors may no longer apply
    setFormErrors(null);
    handleSubmit((values) => {
      // the recipient's own section hasn't saved, so there's nothing to attach these to
      if (!recipient) {
        setErrorMessage(l10n.getString("error-generic"));
        return;
      }
      handleCreate(
        values,
        () => {
          reset(getValues());
          onSaved();
        },
        handleSaveError
      );
    })();
  };

  const handleClickCancel = () => {
    reset();
    onCancel();
  };

  const renderAddress = (fieldName: SwiftAddressField, titleId?: string) => (
    <AddressFields
      control={control}
      fieldName={fieldName}
      title={titleId ? l10n.getString(titleId) : undefined}
      renameFields={lookups.addressRenameFields[fieldName]}
      errors={saveErrors?.[fieldName] as AddressErrors | undefined}
      countryError={lookups.addressRenameErrors[fieldName]}
      onCountryChange={(countryCode) =>
        lookups.onCountryChange(fieldName, countryCode)
      }
      onAutocomplete={(update) =>
        lookups.onAddressAutocomplete(fieldName, update)
      }
    />
  );

  return (
    <ResponsiveFlex gapSize="xl">
      {showMissingDetailsAlert || errorMessage || showUnsavedError ? (
        <ResponsiveFlex gapSize="s">
          {showMissingDetailsAlert ? (
            <Alert
              kind="warn"
              isActive
              icon="alert-triangle"
              isDismissable={false}
            >
              <div className="fontSize--s">
                {l10n.getString("add-swift-details-alert")}
              </div>
            </Alert>
          ) : null}
          <SectionError
            errorMessage={errorMessage}
            showUnsavedError={showUnsavedError}
          />
        </ResponsiveFlex>
      ) : null}
      {renderAddress("address")}
      <Row gapSize="s" alignItems="center">
        <Controller
          name="isCorrespondentBankEnabled"
          control={control}
          render={({ field }) => (
            <Toggle
              isActive={field.value}
              onChange={(isActive: boolean) => {
                field.onChange(isActive);
                lookups.onCorrespondentBankToggle(isActive);
              }}
              labelledBy="correspondent-bank-toggle-label"
            />
          )}
        />
        <label
          id="correspondent-bank-toggle-label"
          className="fontWeight--semibold"
        >
          {l10n.getString(
            "international-wire-drawer-correspondent-bank-toggle"
          )}
        </label>
      </Row>
      {lookups.isCorrespondentBankEnabled
        ? renderAddress(
            "branch_address",
            "international-wire-drawer-beneficiary-bank-address"
          )
        : null}
      <BeneficiaryBankFields
        control={control}
        isCorrespondentBankEnabled={Boolean(lookups.isCorrespondentBankEnabled)}
        bank={lookups.beneficiaryBank}
        isLoadingBank={lookups.isLoadingBeneficiaryBank}
        bicError={lookups.beneficiaryBicError}
        getFieldError={getFieldError}
        onBicBlur={lookups.onBeneficiaryBicBlur}
        renderBranchAddress={() =>
          renderAddress(
            "branch_address",
            "international-wire-drawer-branch-address"
          )
        }
      />
      {lookups.isCorrespondentBankEnabled ? (
        <CorrespondentBankFields
          control={control}
          institution={lookups.correspondentInstitution}
          isLoadingInstitution={lookups.isLoadingCorrespondentBank}
          bicError={lookups.correspondentBicError}
          apiError={getFieldError("correspondent_bank_bic")}
          onBicBlur={lookups.onCorrespondentBicBlur}
        />
      ) : null}
      <SectionActions
        testIdSuffix="international-wire"
        isSaving={loading}
        onCancel={handleClickCancel}
        onSave={handleClickSave}
      />
    </ResponsiveFlex>
  );
};

export default InternationalWireForm;
